import type { FunnelStageMetrics } from '../../../shared/models/funnel-analytics.models';

/** Minimum visual width as a ratio of the top stage (45–55% target). */
export const FUNNEL_MIN_WIDTH_RATIO = 0.5;

export const FUNNEL_STAGE_COLORS = [
  'var(--cg-funnel-stage-1)',
  'var(--cg-funnel-stage-2)',
  'var(--cg-funnel-stage-3)',
  'var(--cg-funnel-stage-4)',
  'var(--cg-funnel-stage-5)',
] as const;

export type FunnelStageViewModel = FunnelStageMetrics & {
  visualTopHalf: number;
  visualBottomHalf: number;
  displayConversionFromFirst: number;
  isEmpty: boolean;
  isLongName: boolean;
};

/**
 * Position-based taper widths for a classic CRM funnel silhouette.
 * First stage is always 100%; the last stage lands at the minimum ratio.
 */
export function buildFunnelVisualWidths(stageCount: number): number[] {
  if (stageCount === 0) {
    return [];
  }
  if (stageCount === 1) {
    return [100];
  }

  const minPercent = FUNNEL_MIN_WIDTH_RATIO * 100;
  return Array.from({ length: stageCount }, (_, index) =>
    Math.round((100 - (index / (stageCount - 1)) * (100 - minPercent)) * 10) / 10,
  );
}

/**
 * Builds view models for the connected trapezoid funnel chart.
 */
export function buildFunnelStageViewModels(stages: FunnelStageMetrics[]): FunnelStageViewModel[] {
  const widths = buildFunnelVisualWidths(stages.length);
  const firstCount = stages[0]?.count ?? 0;

  return stages.map((stage, index) => {
    const topWidth = widths[index];
    const bottomWidth = index < widths.length - 1 ? widths[index + 1] : topWidth * 0.92;

    return {
      ...stage,
      visualTopHalf: topWidth / 2,
      visualBottomHalf: bottomWidth / 2,
      displayConversionFromFirst:
        firstCount > 0 ? Math.round((stage.count / firstCount) * 1000) / 10 : 0,
      isEmpty: stage.count === 0,
      isLongName: stage.name.trim().length > 18,
    };
  });
}

export function funnelStageColor(index: number): string {
  return FUNNEL_STAGE_COLORS[index % FUNNEL_STAGE_COLORS.length];
}

export function formatFunnelCount(count: number): string {
  return count.toLocaleString();
}

export function formatFunnelRate(rate: number | null): string {
  if (rate === null) {
    return '—';
  }
  return `${rate}%`;
}

export function formatFunnelCountWithRate(count: number, rate: number): string {
  return `${formatFunnelCount(count)} (${formatFunnelRate(rate)})`;
}

export type FunnelTooltipLines = {
  stageName: string;
  count: string;
  percentOfTotal: string;
  previousConversion: string;
  overallConversion: string;
};

export function buildFunnelTooltip(stage: FunnelStageMetrics): FunnelTooltipLines {
  return {
    stageName: stage.name,
    count: formatFunnelCount(stage.count),
    percentOfTotal: formatFunnelRate(stage.percentOfTotal),
    previousConversion: formatFunnelRate(stage.conversionFromPrevious),
    overallConversion: formatFunnelRate(stage.conversionFromFirst),
  };
}

export function funnelSegmentClipPath(topHalf: number, bottomHalf: number): string {
  return `polygon(calc(50% - ${topHalf}%) 0, calc(50% + ${topHalf}%) 0, calc(50% + ${bottomHalf}%) 100%, calc(50% - ${bottomHalf}%) 100%)`;
}
